import {
  array,
  InferInput,
  InferOutput,
  number,
  object,
  optional,
  pipe,
  safeParse,
  string,
  transform,
  union,
  ValiError,
} from 'valibot';
import {
  AlbumItemSchema,
  PlaylistItemSchema,
  StationItemSchema,
} from '../apiSchema.ts';
import { getOffsetNumber } from '../utils.ts';

/**
 * Schema for the parameters of the `/v1/me/library/recently-added` endpoint.
 */
export const MeLibraryRecentlyAddedRequestArgsSchema = pipe(
  object({
    maxRecent: optional(number()),
    offset: optional(number()),
  }),
  transform(({ maxRecent, offset }) => {
    return {
      ...(maxRecent ? { limit: maxRecent } : {}),
      ...(offset ? { offset } : {}),
    };
  }),
);

/**
 * Type for the parameters of the `/v1/me/library/recently-added` endpoint.
 */
export type MeLibraryRecentlyAddedRequestArgs = InferInput<
  typeof MeLibraryRecentlyAddedRequestArgsSchema
>;

/**
 * Type for the internal parameters of the `/v1/me/library/recently-added` endpoint.
 */
export type MeLibraryRecentlyAddedRequestArgsOutput = InferOutput<
  typeof MeLibraryRecentlyAddedRequestArgsSchema
>;

/**
 * Schema for the response of the `/v1/me/library/recently-added` endpoint.
 */
export const MeLibraryRecentlyAddedResponseSchema = pipe(
  object({
    data: array(
      union([AlbumItemSchema, PlaylistItemSchema, StationItemSchema]),
    ),
    next: optional(string()),
  }),
  transform(({ data, next }) => {
    if (!next) {
      return {
        recentlyAdded: data,
      };
    }
    const offset = getOffsetNumber(next);
    return {
      recentlyAdded: data,
      next: offset,
    };
  }),
);

/**
 * Type for the original response of the `/v1/me/library/recently-added` endpoint.
 */
export type MeLibraryRecentlyAddedResponse = InferInput<
  typeof MeLibraryRecentlyAddedResponseSchema
>;

/**
 * Type for the normalized response data of the `/v1/me/library/recently-added` endpoint.
 */
export type MeLibraryRecentlyAddedData = InferOutput<
  typeof MeLibraryRecentlyAddedResponseSchema
>;

/**
 * Schema for the musickit API response of the `/v1/me/library/recently-added` endpoint.
 */
export const MeLibraryRecentlyAddedAPISchema = object({
  data: MeLibraryRecentlyAddedResponseSchema,
});

/**
 * Type for the musickit API response of the `/v1/me/library/recently-added` endpoint.
 */
export type MeLibraryRecentlyAddedAPI = InferOutput<
  typeof MeLibraryRecentlyAddedAPISchema
>;

/**
 * Request the recently added items from the user's library.
 * @param {MusicKit.MusicKitInstance} player The MusicKit instance.
 * @param {MeLibraryRecentlyAddedRequestArgs} params The parameters for the request.
 * @returns {Promise<MeLibraryRecentlyAddedData>} The response data.
 */
export const $getMeLibraryRecentlyAdded = async (
  player: MusicKit.MusicKitInstance,
  params: MeLibraryRecentlyAddedRequestArgs,
): Promise<MeLibraryRecentlyAddedData> => {
  const p = safeParse(MeLibraryRecentlyAddedRequestArgsSchema, params);
  if (!p.success) {
    throw new ValiError(p.issues);
  }
  const parsedParams = p.output;

  const res = await player.api.music(
    '/v1/me/library/recently-added',
    parsedParams,
  ) as unknown as MeLibraryRecentlyAddedAPI;

  const validatedRes = safeParse(
    MeLibraryRecentlyAddedAPISchema,
    res,
  );
  if (!validatedRes.success) {
    console.error(
      '[/v1/me/library/recently-added] API response validation failed:',
      JSON.stringify(validatedRes.issues, null, 2),
    );
    throw new ValiError(validatedRes.issues);
  }

  return validatedRes.output.data;
};
